<script setup lang="ts">
import { ref, computed } from 'vue';
import type { Laboratory } from '../../types';
import Modal from '../Modal.vue';
import WhatsAppPreviewModal from './WhatsAppPreviewModal.vue';

const props = defineProps<{
  show: boolean;
  laboratory: Laboratory;
}>();

const emit = defineEmits<{
  close: [];
}>();

interface Crystal {
  workNumber: string;
  material: string;
  spherical: string;
  cylindrical: string;
  quantity: string;
}

const initialState = {
  currentCrystal: {
    workNumber: '',
    material: '',
    spherical: '',
    cylindrical: '',
    quantity: '1 Par'
  },
  currentCrystals: [] as Crystal[],
  orders: [] as Array<{ workNumber: string; crystals: Crystal[] }>,
  showPreview: false
};

const branch = ref('');
const currentCrystal = ref({ ...initialState.currentCrystal });
const orders = ref([...initialState.orders]);
const currentCrystals = ref([...initialState.currentCrystals]);
const sphericalInput = ref<HTMLInputElement | null>(null);
const showPreviewModal = ref(false);
const sending = ref(false);
const editingOrderIndex = ref<number | null>(null);

const quantitySuggestions = ['1/2', '1 Par', '1 Par y 1/2', '2 Pares'];

const resetModal = () => {
  currentCrystal.value = { ...initialState.currentCrystal };
  orders.value = [...initialState.orders];
  currentCrystals.value = [...initialState.currentCrystals];
  showPreviewModal.value = false;
  editingOrderIndex.value = null;
};

const addCrystal = () => {
  currentCrystals.value.push({ ...currentCrystal.value });
  // Mantener número de trabajo y material, resetear solo esférico y cilíndrico
  const { workNumber, material, quantity } = currentCrystal.value;
  currentCrystal.value = {
    workNumber,
    material,
    spherical: '',
    cylindrical: '',
    quantity
  };
  
  setTimeout(() => {
    if (sphericalInput.value) {
      sphericalInput.value.focus();
    }
  }, 0);
};

const editCrystal = (crystalIndex: number) => {
  const crystal = currentCrystals.value[crystalIndex];
  currentCrystal.value = { ...crystal };
  currentCrystals.value.splice(crystalIndex, 1);
};

const deleteCrystal = (crystalIndex: number) => {
  if (confirm('¿Está seguro de que desea eliminar este cristal?')) {
    currentCrystals.value.splice(crystalIndex, 1);
  }
};

const addOrder = () => {
  if (currentCrystals.value.length > 0) {
    if (editingOrderIndex.value !== null) {
      orders.value[editingOrderIndex.value] = {
        workNumber: currentCrystal.value.workNumber,
        crystals: [...currentCrystals.value]
      };
      editingOrderIndex.value = null;
    } else {
      orders.value.push({
        workNumber: currentCrystal.value.workNumber,
        crystals: [...currentCrystals.value]
      });
    }
    currentCrystals.value = [];
    currentCrystal.value = {
      workNumber: '',
      material: '',
      spherical: '',
      cylindrical: '',
      quantity: '1 Par'
    };
  }
};

const editOrder = (index: number) => {
  const order = orders.value[index];
  currentCrystal.value = {
    workNumber: order.workNumber,
    material: order.crystals[0]?.material || '',
    spherical: '',
    cylindrical: '',
    quantity: '1 Par'
  };
  currentCrystals.value = [...order.crystals];
  editingOrderIndex.value = index;
  showPreviewModal.value = false;
};

const deleteOrder = (index: number) => {
  if (confirm('¿Está seguro de que desea eliminar este pedido?')) {
    orders.value.splice(index, 1);
  }
};

const groupCrystalsByMaterial = (crystals: Crystal[]) => {
  const grouped = new Map<string, Crystal[]>();
  
  crystals.forEach(crystal => {
    const key = crystal.material;
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(crystal);
  });
  
  return grouped;
};

const previewMessage = computed(() => {
  let message = '';
  
  // Add branch information at the start
  if (branch.value) {
    message += `*Pedido desde Sucursal: ${branch.value}*\n\n`;
  }
  
  // Add orders in queue
  orders.value.forEach((order) => {
    message += `*Trabajo ${order.workNumber}*\n`;
    const groupedCrystals = groupCrystalsByMaterial(order.crystals);
    
    groupedCrystals.forEach((crystals, material) => {
      message += `${material}:\n`;
      crystals.forEach(crystal => {
        message += `• ${crystal.quantity} | Esf: ${crystal.spherical} Cil: ${crystal.cylindrical}\n`;
      });
    });
    message += '\n';
  });

  // Add current order if there are crystals
  if (currentCrystals.value.length > 0) {
    message += `*Trabajo ${currentCrystal.value.workNumber}*\n`;
    const groupedCrystals = groupCrystalsByMaterial(currentCrystals.value);
    
    groupedCrystals.forEach((crystals, material) => {
      message += `${material}:\n`;
      crystals.forEach(crystal => {
        message += `• ${crystal.quantity} | Esf: ${crystal.spherical} Cil: ${crystal.cylindrical}\n`;
      });
    });
  }

  return message;
});

const handleClose = () => {
  resetModal();
  emit('close');
};

const sendOrder = () => {
  if (orders.value.length === 0 && currentCrystals.value.length === 0) {
    return;
  }

  sending.value = true;
  try {
    if (currentCrystals.value.length > 0) {
      addOrder();
    }

    const encodedMessage = encodeURIComponent(previewMessage.value);
    window.open(`https://wa.me/${props.laboratory.WhatsApp}?text=${encodedMessage}`, '_blank');
    handleClose();
  } finally {
    sending.value = false;
  }
};

const handlePreview = () => {
  if (currentCrystals.value.length > 0) {
    addOrder();
  }
  showPreviewModal.value = true;
};
</script>

<template>
  <Modal
    :show="show"
    :title="`Nuevo Pedido - ${laboratory.Nombre}`"
    @close="handleClose"
  >
    <div class="grid grid-cols-5 gap-6">
      <!-- Left side: Form -->
      <div class="col-span-3 space-y-6">
        <form class="space-y-6">
          <!-- Sucursal input -->
          <div class="bg-white rounded-lg border border-slate-200 p-4">
            <label class="block text-sm font-medium text-slate-700 mb-2">Sucursal</label>
            <input
              v-model="branch"
              type="text"
              class="input-field"
              placeholder="Ingrese la sucursal"
              required
            />
          </div>

          <!-- Formulario de cristal actual -->
          <div class="bg-white rounded-lg border border-slate-200 p-4 space-y-4">
            <h3 class="text-lg font-medium text-slate-800">Datos del Cristal</h3>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">Número de Trabajo</label>
                <input
                  v-model="currentCrystal.workNumber"
                  type="text"
                  class="input-field"
                  placeholder="Ej: 001"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">Material</label>
                <input
                  v-model="currentCrystal.material"
                  type="text"
                  class="input-field"
                  placeholder="Ej: Orgánico"
                />
              </div>
            </div>

            <!-- Inputs en línea para cantidad, esférico y cilíndrico -->
            <div class="flex gap-4 items-end">
              <div class="w-1/4">
                <label class="block text-sm font-medium text-slate-700 mb-2">Cantidad</label>
                <input
                  v-model="currentCrystal.quantity"
                  type="text"
                  class="input-field"
                  list="quantityOptions"
                  placeholder="Ej: 1 Par"
                />
                <datalist id="quantityOptions">
                  <option v-for="option in quantitySuggestions" :key="option" :value="option" />
                </datalist>
              </div>

              <div class="w-1/3">
                <label class="block text-sm font-medium text-slate-700 mb-2">Esférico</label>
                <input
                  ref="sphericalInput"
                  v-model="currentCrystal.spherical"
                  type="text"
                  class="input-field"
                  placeholder="Ej: +2.00 o -2.00"
                />
              </div>

              <div class="w-1/3">
                <label class="block text-sm font-medium text-slate-700 mb-2">Cilíndrico</label>
                <input
                  v-model="currentCrystal.cylindrical"
                  type="text"
                  class="input-field"
                  placeholder="Ej: +2.00 o -2.00"
                />
              </div>

              <div class="flex-shrink-0">
                <button
                  type="button"
                  @click="addCrystal"
                  class="btn-primary h-[42px]"
                  :disabled="!currentCrystal.workNumber || !currentCrystal.material || 
                            !currentCrystal.spherical || !currentCrystal.cylindrical"
                >
                  Agregar Cristal
                </button>
              </div>
            </div>
          </div>

          <!-- Lista de cristales actuales -->
          <div v-if="currentCrystals.length > 0" class="bg-white rounded-lg border border-slate-200 p-4">
            <div class="flex justify-between items-center mb-4">
              <h4 class="text-lg font-medium text-slate-800">
                Cristales en Trabajo {{ currentCrystal.workNumber }}
                <span v-if="editingOrderIndex !== null" class="text-sm text-indigo-600">
                  (Editando)
                </span>
              </h4>
              <button
                type="button"
                @click="addOrder"
                class="btn-primary"
              >
                {{ editingOrderIndex !== null ? 'Guardar Cambios' : 'Agregar al Pedido' }}
              </button>
            </div>

            <div class="space-y-4">
              <template v-for="[material, crystals] in groupCrystalsByMaterial(currentCrystals)" :key="material">
                <div class="border-l-2 border-indigo-200 pl-4">
                  <h5 class="font-medium text-slate-700">{{ material }}</h5>
                  <div class="space-y-2 mt-2">
                    <div 
                      v-for="(crystal, index) in crystals" 
                      :key="index"
                      class="flex items-center justify-between group bg-slate-50 p-2 rounded-lg"
                    >
                      <span class="text-sm text-slate-600">
                        {{ crystal.quantity }} | Esf: {{ crystal.spherical }}, Cil: {{ crystal.cylindrical }}
                      </span>
                      <div class="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          @click="editCrystal(currentCrystals.findIndex(c => c === crystal))"
                          class="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          @click="deleteCrystal(currentCrystals.findIndex(c => c === crystal))"
                          class="text-xs px-2 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </form>
      </div>

      <!-- Right side: Orders queue -->
      <div class="col-span-2">
        <div class="sticky top-4">
          <div v-if="orders.length > 0" class="bg-white rounded-lg border border-slate-200 p-4">
            <h4 class="text-lg font-medium text-slate-800 mb-4">Pedidos en Cola</h4>
            <div class="space-y-4 max-h-[600px] overflow-y-auto">
              <div v-for="(order, orderIndex) in orders" :key="orderIndex" 
                   class="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div class="flex justify-between items-center mb-3">
                  <h5 class="font-medium text-slate-800">Trabajo {{ order.workNumber }}</h5>
                  <div class="flex gap-2">
                    <button
                      type="button"
                      @click="editOrder(orderIndex)"
                      class="text-sm px-3 py-1 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      @click="deleteOrder(orderIndex)"
                      class="text-sm px-3 py-1 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>

                <div class="space-y-3">
                  <template v-for="[material, crystals] in groupCrystalsByMaterial(order.crystals)" :key="material">
                    <div class="border-l-2 border-slate-300 pl-3">
                      <h6 class="font-medium text-slate-700">{{ material }}</h6>
                      <div class="mt-1 space-y-1">
                        <p v-for="(crystal, crystalIndex) in crystals" :key="crystalIndex" 
                           class="text-sm text-slate-600">
                          {{ crystal.quantity }} | Esf: {{ crystal.spherical }}, Cil: {{ crystal.cylindrical }}
                        </p>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="bg-white rounded-lg border border-slate-200 p-4 text-center text-slate-600">
            No hay pedidos en cola
          </div>
        </div>
      </div>
    </div>

    <!-- Action buttons -->
    <div class="flex justify-end gap-4 pt-4 mt-6 border-t">
      <button
        type="button"
        @click="handleClose"
        class="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium"
      >
        Cancelar
      </button>
      <button
        type="button"
        @click="handlePreview"
        class="btn-primary"
        :disabled="currentCrystals.length === 0 && orders.length === 0"
      >
        Ver Vista Previa
      </button>
    </div>
  </Modal>

  <!-- Preview Modal -->
  <WhatsAppPreviewModal
    :show="showPreviewModal"
    :message="previewMessage"
    :on-send="sendOrder"
    @close="showPreviewModal = false"
  />
</template>